'use client';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { useColorScheme } from '@mui/material/styles';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';

export interface ColorSchemeToggleProps {
  /** `menu`: full-width row for dropdowns. `toolbar`: responsive. `footer`: compact for the footer bar. */
  layout?: 'toolbar' | 'menu' | 'footer';
}

export default function ColorSchemeToggle({
  layout = 'toolbar',
}: ColorSchemeToggleProps) {
  const { mode, setMode } = useColorScheme();
  const isMenu = layout === 'menu';
  const isFooter = layout === 'footer';

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: 'light' | 'dark' | 'system' | null,
  ) => {
    if (value != null) {
      setMode(value);
    }
  };

  return (
    <ToggleButtonGroup
      exclusive
      value={mode ?? 'system'}
      onChange={handleChange}
      size="small"
      aria-label="Theme mode"
      sx={{
        mr: isFooter ? { xs: 0, sm: '20px' } : '20px',

        width: isFooter ? 'auto' : isMenu ? '100%' : { xs: '100%', md: 'auto' },
        minHeight: isFooter ? 0 : undefined,
        '& .MuiToggleButton-root': {
          flex: isFooter ? 'initial' : isMenu ? 1 : { xs: 1, md: 'initial' },
          px: isFooter ? 0.5 : isMenu ? 0 : { xs: 0, md: 1 },
          py: isFooter ? 0.125 : isMenu ? 0.25 : 0.5,
          minWidth: isFooter ? 0 : undefined,
          '& .MuiSvgIcon-root': {
            fontSize: isFooter ? '14px' : isMenu ? '1rem' : undefined,
          },
        },
      }}
    >
      <Tooltip title="Match system">
        <ToggleButton value="system" aria-label="Use system color scheme">
          <SettingsBrightnessOutlinedIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Light">
        <ToggleButton value="light" aria-label="Light mode">
          <LightModeOutlinedIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Dark">
        <ToggleButton value="dark" aria-label="Dark mode">
          <DarkModeOutlinedIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );
}
